import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchHangars,
  fetchHangar,
  createHangar,
  updateHangar,
  deleteArea,
  fetchHangarsWithUnitsForLocation,
} from '@/lib/api/hangars'
import { broadcastInvalidation } from '@/lib/sync'
import { unitKeys } from '@/hooks/useUnits'
import type { HangarInsert, HangarUpdate, HangarFilters } from '@/types/hangars'
import { toast } from 'sonner'

/**
 * Query key factory for hangars
 * Provides consistent keys for cache management
 */
export const hangarKeys = {
  all: ['hangars'] as const,
  lists: () => [...hangarKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: HangarFilters) =>
    [...hangarKeys.lists(), organizationId, filters] as const,
  details: () => [...hangarKeys.all, 'detail'] as const,
  detail: (id: string) => [...hangarKeys.details(), id] as const,
}

/**
 * Fetch all hangars for current organization
 * Optionally filter by location
 */
export function useHangars(filters?: HangarFilters) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: hangarKeys.list(organizationId || '', filters),
    queryFn: () => fetchHangars(organizationId!, filters),
    enabled: !!organizationId,
  })
}

/**
 * Fetch hangars for a specific location
 */
export function useHangarsByLocation(locationId: string | undefined) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id
  const filters: HangarFilters = { locationId }

  return useQuery({
    queryKey: hangarKeys.list(organizationId || '', filters),
    queryFn: () => fetchHangars(organizationId!, filters),
    enabled: !!organizationId && !!locationId,
  })
}

/**
 * Fetch a single hangar by ID
 */
export function useHangar(id: string | undefined) {
  return useQuery({
    queryKey: hangarKeys.detail(id || ''),
    queryFn: () => fetchHangar(id!),
    enabled: !!id,
  })
}

/**
 * Create a new hangar
 */
export function useCreateHangar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: HangarInsert) => createHangar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hangarKeys.lists() })
      broadcastInvalidation([hangarKeys.lists()])
      toast.success('Hangar created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create hangar', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing hangar
 */
export function useUpdateHangar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: HangarUpdate }) =>
      updateHangar(id, data),
    onSuccess: (_, { id }) => {
      const keys = [hangarKeys.lists(), hangarKeys.detail(id)] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Hangar updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update hangar', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete an area and all its child units (soft delete via DB function)
 */
export function useDeleteHangar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteArea(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: hangarKeys.all })
      queryClient.invalidateQueries({ queryKey: unitKeys.all })
      broadcastInvalidation([hangarKeys.all, unitKeys.all])
      const unitMsg = result.units_deleted > 0
        ? ` and ${result.units_deleted} unit${result.units_deleted !== 1 ? 's' : ''}`
        : ''
      toast.success(`Area${unitMsg} removed successfully`)
    },
    onError: (error: Error) => {
      toast.error('Failed to remove area', {
        description: error.message,
      })
    },
  })
}

/**
 * Fetch hangars with their units for 3D rendering
 * Includes all unit data needed for template-aware rendering
 */
export function useHangarsWithUnitsFor3D(locationId: string | undefined) {
  return useQuery({
    queryKey: [...hangarKeys.all, '3d', locationId],
    queryFn: () => fetchHangarsWithUnitsForLocation(locationId!),
    enabled: !!locationId,
  })
}
